import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const LOGO_URL = "/assets/images/logo.png";

// Messaging app icons
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const KakaoTalkIcon = () => (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
    <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 01-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3z"/>
  </svg>
);

const WeChatIcon = () => (
  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348z"/>
  </svg>
);

const Footer = () => {
  const { t, positioning } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="Kaesthetic World" 
              className="h-16 w-auto object-contain mb-8"
              data-testid="footer-logo"
            />
            <p className="text-zinc-500 text-sm leading-relaxed max-w-sm mb-8 font-light">
              {t.footer.tagline}
            </p>
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <MapPin className="w-3.5 h-3.5" />
              <span data-testid="footer-location">{positioning.companyLocation}</span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase text-zinc-400 mb-6">{t.nav.contact}</h4>
            <ul className="space-y-5">
              <li>
                <a 
                  href="mailto:info@kaesthetic.world" 
                  className="flex items-center gap-3 text-zinc-500 hover:text-white transition-colors text-sm"
                >
                  <Mail className="w-4 h-4" />
                  info@kaesthetic.world
                </a>
              </li>
              <li>
                <div className="flex items-center gap-3 text-zinc-500 text-sm">
                  <Phone className="w-4 h-4" />
                  <div>
                    <a href="tel:+34662408398" className="hover:text-white transition-colors block">
                      +34 662 40 83 98
                    </a>
                    <span className="text-xs text-zinc-600 flex items-center gap-2 mt-1">
                      <WhatsAppIcon /> WhatsApp
                    </span>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-center gap-3 text-zinc-500 text-sm">
                  <Phone className="w-4 h-4" />
                  <div>
                    <a href="tel:+821044538035" className="hover:text-white transition-colors block">
                      +82 10 4453 8035
                    </a>
                    <span className="text-xs text-zinc-600 flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1"><KakaoTalkIcon /> KakaoTalk</span>
                      <span className="flex items-center gap-1"><WeChatIcon /> WeChat</span>
                    </span>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase text-zinc-400 mb-6">Navigation</h4>
            <ul className="space-y-4">
              <li><a href="#what-we-do" className="text-zinc-500 hover:text-white transition-colors text-sm">{t.nav.whatWeDo}</a></li>
              <li><a href="#experiences" className="text-zinc-500 hover:text-white transition-colors text-sm">{t.nav.experiences}</a></li>
              <li><a href="#services" className="text-zinc-500 hover:text-white transition-colors text-sm">{t.nav.services}</a></li>
              <li><a href="#why-us" className="text-zinc-500 hover:text-white transition-colors text-sm">{t.nav.whyUs}</a></li>
              <li><a href="#contact" className="text-zinc-500 hover:text-white transition-colors text-sm">{t.nav.contact}</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-20 pt-8 border-t border-white/5">
          <p className="text-zinc-600 text-xs tracking-wider" data-testid="footer-copyright">
            © {currentYear} Kaesthetic World. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
